import { useEffect } from 'react';
import { useShadowRoot } from '@backstage/plugin-techdocs-react';

/**
 * Identifier for our injected `<style>` so we don't add a duplicate node
 * every time the reader re-renders (the addon re-runs on every nav between
 * docs pages within the same entity, since the shadow root is recreated).
 */
const FIX_STYLE_ID = 'bosch-techdocs-mkdocs-layout-fix';

/**
 * CSS we inject INSIDE the TechDocs Shadow DOM.
 *
 * Why this exists — the layered story:
 *
 *   1) MkDocs Material ships the docs nav (left) and the in-page TOC
 *      (right) as `.md-sidebar` elements with `position: sticky;
 *      top: 2.4rem;` — they normally flow with the content and stick
 *      to the top of the viewport with a small offset to clear the
 *      upstream MkDocs navbar.
 *   2) Backstage hides that MkDocs navbar (`.md-header { display: none }`)
 *      AND, in
 *      `node_modules/@backstage/plugin-techdocs/dist/reader/transformers/styles/rules/layout.esm.js`,
 *      replaces the sticky positioning with
 *        position: fixed;
 *        bottom: 75px;
 *        width: 16rem;
 *      so the sidebars stay anchored to the browser viewport during
 *      scroll. Combined with `.md-content { margin-left: 16rem }`,
 *      the upstream layout is built around the assumption that the
 *      sidebar is taken out of flow and the content makes room for
 *      it via margin.
 *   3) Our `BoschPageLayout` renders a tall blue banner + the BUI
 *      `<Header>` (title + "Search docs" input) + the entity-card row
 *      (System / Owner / Lifecycle) ABOVE the docs body. With
 *      `position: fixed`, when the user scrolls past that chrome the
 *      sidebars stay floating in mid-viewport with a big empty band
 *      above them — they look detached from the docs they describe.
 *
 * What we want
 * ------------
 * The user wants the sidebars to live ALONGSIDE the H1 at the top of
 * the docs body and to scroll AWAY together with the rest of the page
 * — i.e. behave like normal in-flow content. They are part of the
 * docs intro, not a permanent navigation chrome.
 *
 * How we achieve it
 * -----------------
 *   - Override `position: fixed` -> `position: static`. The sidebars
 *     re-enter the natural flex flow of `.md-main__inner` and sit on
 *     either side of `.md-content`. Because they're now in flow, they
 *     scroll with the page — exactly the desired behaviour.
 *   - Reset `top`, `bottom`, `height` so the upstream's fixed-layout
 *     constraints don't fight us. Auto height lets each sidebar size
 *     to its own content.
 *   - Reset `.md-content { margin-left: 0; max-width: none }` because
 *     the upstream margin/max-width on `.md-content` only existed to
 *     reserve a 16rem column for the FIXED left sidebar. With the
 *     sidebar back in flex flow it already takes its 16rem, so the
 *     margin would double-count and leave a 16rem gap between the
 *     left sidebar and the content.
 *   - Reset `position: fixed` on the special inner-content sidebar
 *     selector `.md-content > .md-sidebar` (used by some MkDocs
 *     templates) so it follows the same in-flow rule.
 *
 * Selector breadth: in MkDocs Material the sidebars come in two
 * flavours — `.md-sidebar--primary` (left, the docs nav) and
 * `.md-sidebar--secondary` (right, the page TOC). They both inherit
 * `.md-sidebar`, but a future upstream upgrade could split the rules
 * onto the modifier class only, in which case a bare `.md-sidebar`
 * selector would silently stop matching. Targeting all three ensures
 * the override sticks even after that kind of renaming.
 *
 * If a future BUI or MkDocs Material upgrade renames `.md-sidebar`
 * altogether, this rule becomes a no-op (because no selector
 * matches) — the worst case is the upstream `position: fixed`
 * coming back, which only changes the scroll feel and doesn't break
 * the page. After such an upgrade, just point the selector at
 * whatever the new sidebar class is.
 */
const FIX_CSS = `
  /*
   * Take the MkDocs sidebars (left = docs nav, right = page TOC) out
   * of \`position: fixed\` and put them back into the natural flex
   * flow of \`.md-main__inner\`. Result: they sit alongside the H1
   * at the top of the docs body and scroll AWAY together with the
   * rest of the page when the user scrolls — instead of floating
   * mid-viewport with empty space above them once the chrome
   * scrolls past.
   *
   * \`!important\` is required on every property because the
   * upstream techdocs reader injects its \`position: fixed; bottom:
   * 75px; width: 16rem;\` block AFTER MkDocs Material's bundled
   * stylesheet. Without \`!important\` the cascade puts upstream's
   * fixed positioning on top of our static override and the bug
   * comes back.
   */
  .md-sidebar,
  .md-sidebar--primary,
  .md-sidebar--secondary,
  .md-content > .md-sidebar {
    position: static !important;
    top: auto !important;
    bottom: auto !important;
    left: auto !important;
    right: auto !important;
    height: auto !important;
  }

  /*
   * The upstream techdocs layout reserves a 16rem column on the left
   * for the FIXED sidebar via \`margin-left\` and constrains the
   * content with \`max-width: calc(100% - 16rem * 2)\`. With the
   * sidebars back in the flex flow they already take their 16rem,
   * so leaving the upstream margin in place would double-count and
   * produce a 16rem empty band between the left sidebar and the
   * content. Reset \`margin-left\` so the flex layout decides
   * column placement.
   *
   * \`max-width\` matters too but for a different reason: with the
   * upstream's fixed-layout calc gone, an unconstrained
   * \`.md-content\` grows to fill 100% of the flex container.
   * Result: prose lines stretch the full width of the flex track,
   * blockquotes / admonitions go off the right edge of the
   * viewport on wide screens, and the page acquires a horizontal
   * scrollbar. Cap the column at a reading-friendly width
   * (~45rem ≈ 720px) so paragraphs wrap automatically. 45rem
   * matches the "comfortable reading line" used by React docs
   * and Stripe (~720px) — narrow enough for prose to feel snug
   * without becoming claustrophobic.
   *
   * \`min-width: 0\` is a flexbox quirk fix: a flex item's
   * \`min-width\` defaults to \`auto\`, which is "the min-content
   * size of the item" — for a paragraph that's the longest
   * unbreakable token (a URL, an inline code span, …). Without
   * \`min-width: 0\` the column refuses to shrink below that
   * intrinsic min, so on narrow viewports the column can still
   * push past the viewport edge even with our \`max-width\` cap.
   * Setting it to 0 lets the column shrink down freely while
   * \`max-width\` still caps it on wide screens.
   *
   * For wide content that genuinely needs to be wide (code blocks,
   * tables) we don't want the cap to clip them. The
   * \`.md-typeset pre/.highlight/table\` rule below keeps those
   * scrolling INSIDE the content column instead of breaking out.
   */
  .md-content {
    margin-left: 0 !important;
    max-width: 45rem !important;
    min-width: 0 !important;
  }

  /*
   * Defensive: code blocks, syntax-highlighted blocks and tables
   * can have intrinsically wide content (long YAML lines, wide
   * tables). Without an explicit overflow rule they would push
   * their parent's width past the \`.md-content { max-width: 60rem }\`
   * cap above (the cap is on the outer column, but a child with
   * \`overflow: visible\` and intrinsic width > parent wins on
   * layout in some flex configurations). Force them to scroll
   * INSIDE the content column instead.
   */
  .md-typeset pre,
  .md-typeset .highlight,
  .md-typeset table {
    max-width: 100% !important;
    overflow-x: auto !important;
  }

  /*
   * Neutralise the upstream \`scrollIntoNavigation\` transformer
   * (\`@backstage/plugin-techdocs/.../transformers/scrollIntoNavigation.esm.js\`).
   * 200ms after every page load it calls \`activeNavItem.scrollIntoView()\`
   * to bring the active item in the LEFT nav into view. With the
   * upstream's \`position: fixed\` sidebar, that item was always
   * visible in the viewport so the call was a no-op. With our
   * \`position: static\` sidebar (see rule above) the item lives in
   * normal flow BELOW the page chrome (banner + BUI Header +
   * entity-card row), so \`scrollIntoView()\` ends up scrolling the
   * BackstagePage \`<main>\` down enough to put the nav item at the
   * top of the viewport — which in practice scrolls the docs H1 +
   * intro text off the screen on every page load. Visible symptom
   * the user reports: "every time I open a doc page it scrolls
   * down to the middle".
   *
   * Trick: \`scroll-margin-top\` adds a "safe zone" ABOVE the target
   * when the browser scrolls it into view. With a value larger than
   * any plausible viewport height (100vh works on every screen the
   * portal will ever render on), the scroll position the browser
   * would need to satisfy the margin is NEGATIVE (\`element.offsetTop
   * - 100vh\`), and \`scrollTop\` clamps to 0 — so the call becomes a
   * no-op. We don't lose anything: the nav item is already visible
   * at page top with our static sidebars.
   *
   * Targets both the \`<li>\` (\`md-nav__item--active\`) the upstream
   * transformer queries on, and the \`<a>\` inside it
   * (\`md-nav__link--active\`) for safety in case a future MkDocs
   * Material upgrade swaps which element gets the active class.
   */
  .md-nav__item--active,
  .md-nav__link--active {
    scroll-margin-top: 100vh !important;
  }
`;

/**
 * TechDocs Addon registered at `TechDocsAddonLocations.Content`.
 * Renders nothing visible (`return null`) — its only job is to
 * inject `FIX_CSS` into the shadow root's `<head>` so the override
 * applies INSIDE the shadow tree (global stylesheets in
 * `theme/index.tsx` don't pierce the shadow boundary).
 *
 * Idempotency: we tag the injected `<style>` with `FIX_STYLE_ID` and
 * skip the insertion if a node with that id already exists, so
 * re-renders inside the same shadow root don't stack duplicates. New
 * shadow roots (e.g. navigating to a different entity's docs) are
 * fresh, so the addon re-injects there too.
 *
 * We register it via `AddonBlueprint` in
 * `packages/app/src/modules/techdocs/index.tsx` so it ships alongside
 * the rest of our techdocs override plugin.
 */
export const MkDocsLayoutFix = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) return undefined;

    const head = shadowRoot.querySelector('head');
    if (!head) return undefined;
    if (!head.querySelector(`#${FIX_STYLE_ID}`)) {
      const style = document.createElement('style');
      style.id = FIX_STYLE_ID;
      style.textContent = FIX_CSS;
      head.appendChild(style);
    }

    return undefined;
  }, [shadowRoot]);

  return null;
};
